import { useEffect } from 'react';
import { identify, track } from './tracking';
import type { TrackingEventMap, TrackingEventName } from './events';

// Client-portal tracking. Off until the event page starts it with the analytics id from /auth/me, which the backend
// sends only for a client whose lead authorized tracking. Admins never get one, so nothing they do in a client's event
// (legacy dashboard or the v2 preview) is tracked.

let portalAnalyticsId: string | null = null;

// Time on a tab before it counts as viewed (same as the dashboard's last-section memory)
export const PORTAL_TAB_DWELL_MS = 2000;

export function startPortalTracking(analyticsId: string): void {
  if (portalAnalyticsId === analyticsId) return;
  portalAnalyticsId = analyticsId;
  // Same distinct_id as the quote funnel and the magic-link login
  identify(analyticsId);
}

export function stopPortalTracking(): void {
  portalAnalyticsId = null;
}

export function isPortalTrackingActive(): boolean {
  return portalAnalyticsId !== null;
}

export function trackPortal<E extends TrackingEventName>(event: E, props: TrackingEventMap[E]): void {
  if (!portalAnalyticsId) return;
  track(event, props);
}

export function usePortalTracking(analyticsId: string | null | undefined): void {
  useEffect(() => {
    if (analyticsId) startPortalTracking(analyticsId);
    else stopPortalTracking();
  }, [analyticsId]);
}

/** Fires 'Portal Tab Viewed' once the client has stayed on a tab for a moment */
export function usePortalTabTracking(tab: string): void {
  useEffect(() => {
    if (!tab) return;
    const timer = setTimeout(() => trackPortal('Portal Tab Viewed', { tab }), PORTAL_TAB_DWELL_MS);
    return () => clearTimeout(timer);
  }, [tab]);
}

/** Test-only: forget the identified client. */
export function __resetPortalTrackingForTests(): void {
  portalAnalyticsId = null;
}
